import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import {
  cloneDataOrNull, cloneListOfCloneable, cloneMap, isSameData, isSameList, isSameMap,
  mapToData,
} from '../../dto/data-model-utils.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { BooleanOrListOfString } from '../../core/bool-or-list.js';
import { SeatingOptions } from '../seating-options.js';
import { PassEventValidityRule } from './pass-event-validity-rule.js';
import { SeatedOpenAccessProtocolModifier } from './seated-open-access-protocol-modifier.js';

export enum StageType {
  Required = 'Required',
  Optional = 'Optional',
}

export class StageEntry extends DataModel {
  static readonly TYPE_ID: string = 'fb93fc633dd44c5b82ede98acbbd7029';

  private constructor(
      public readonly uniqueId: string,
      public readonly name: string,
      public readonly roomId: string,
      public readonly eventConfiguration: Map<string, BooleanOrListOfString>,
      public readonly seatingOptions: SeatingOptions | null,
      public readonly modifiers: Map<string, SeatedOpenAccessProtocolModifier>,
  ) {
    super();
  }

  static create(args: {
    uniqueId: string,
    name: string,
    roomId: string,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    seatingOptions: SeatingOptions | null,
    modifiers: Map<string, SeatedOpenAccessProtocolModifier>,
  }): StageEntry {
    return new StageEntry(
        args.uniqueId,
        args.name,
        args.roomId,
        args.eventConfiguration,
        args.seatingOptions,
        args.modifiers,
    );
  }

  get $typeId(): string {
    return StageEntry.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): StageEntry {
    return StageEntry.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      name: MapUtils.getString(map, 'name'),
      roomId: MapUtils.getString(map, 'roomId'),
      eventConfiguration: MapUtils.getMap<string, BooleanOrListOfString>(
          map,
          'eventConfiguration',
          (key, value) => ({ key, value: BooleanOrListOfString.decode(value) }),
      ),
      seatingOptions: context.fromDataOrNull<SeatingOptions>(
          MapUtils.getRecordOrNull(map, 'seatingOptions'),
      ),
      modifiers: MapUtils.getMap<string, SeatedOpenAccessProtocolModifier>(
          map,
          'modifiers',
          (key, value) => ({
            key,
            value: context.fromData<SeatedOpenAccessProtocolModifier>(JSONUtils.decodeRecord(value)),
          }),
      ),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'name': this.name,
      'roomId': this.roomId,
      'eventConfiguration': mapToData(
          this.eventConfiguration,
          (key, value) => ({ key, value: value.encode() }),
      ),
      'seatingOptions': context.toDataOrNull(this.seatingOptions),
      'modifiers': mapToData(
          this.modifiers,
          (key, value) => ({ key, value: context.toData(value) }),
      ),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    name = this.name,
    roomId = this.roomId,
    eventConfiguration = cloneMap(this.eventConfiguration, (value) => value.clone()),
    seatingOptions = cloneDataOrNull(this.seatingOptions),
    modifiers = cloneMap(this.modifiers, (value) => value.clone()),
  }: {
    uniqueId?: string,
    name?: string,
    roomId?: string,
    eventConfiguration?: Map<string, BooleanOrListOfString>,
    seatingOptions?: SeatingOptions | null,
    modifiers?: Map<string, SeatedOpenAccessProtocolModifier>,
  } = {}): this {
    return StageEntry.create({
      uniqueId,
      name,
      roomId,
      eventConfiguration,
      seatingOptions,
      modifiers,
    }) as this;
  }

  isSame(other: StageEntry): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.name === this.name &&
        other.roomId === this.roomId &&
        isSameMap(other.eventConfiguration, this.eventConfiguration, isSameData) &&
        isSameData(other.seatingOptions, this.seatingOptions) &&
        isSameMap(other.modifiers, this.modifiers, isSameData);
  }

  cloneEventConfiguration(): Map<string, BooleanOrListOfString> {
    return cloneMap(this.eventConfiguration, (v) => v.clone());
  }

  isEventAssignedToPass({ eventId, passVariationId, eventValidityRule }: {
    eventId: string,
    passVariationId: string,
    eventValidityRule: PassEventValidityRule,
  }): boolean {
    const entry = this.eventConfiguration.get(eventId);
    const isInEventConfiguration = entry != null && (
      (entry.isList && entry.listValue.includes(passVariationId)) ||
      (entry.isBoolean && entry.booleanValue)
    );

    return eventValidityRule === PassEventValidityRule.IncludedEvents ?
      isInEventConfiguration : !isInEventConfiguration;
  }

  isEventAssignedToTemplate({ eventId, eventValidityRule }: { 
    eventId: string, 
    eventValidityRule: PassEventValidityRule 
  }): boolean {
    const entry = this.eventConfiguration.get(eventId);
    if(eventValidityRule === PassEventValidityRule.IncludedEvents) {
      return entry != null && (
        (entry.isList && entry.listValue.length > 0) ||
        (entry.isBoolean && entry.booleanValue)
      );
    }

    return entry == null || entry.isList || (entry.isBoolean && !entry.booleanValue);
  }

  getAssignedEventIdsForPass({ passVariationId, eventValidityRule }: { 
    passVariationId: string, 
    eventValidityRule: PassEventValidityRule 
  }): string[] {
    const assignedEventIds: string[] = [];
    for(const eventId of this.eventConfiguration.keys()) {
      if(this.isEventAssignedToPass({ eventId, passVariationId, eventValidityRule })) {
        assignedEventIds.push(eventId);
      }
    }

    return assignedEventIds;
  }
}

export class StageEntryBuilder extends DataModelBuilder<StageEntry> {
  build(context: SerializationContext, map: SerializedData): StageEntry {
    return StageEntry.fromMap(context, map);
  }
}

export class Stage extends DataModel {
  static readonly TYPE_ID: string = '6ac619b9c0c3485c920df6dd2192b200';

  private constructor(
      public readonly uniqueId: string,
      public readonly type: StageType,
      public readonly entries: StageEntry[],
  ) {
    super();
  }

  static create(args: {
    uniqueId: string,
    type: StageType,
    entries: StageEntry[],
  }): Stage {
    return new Stage(args.uniqueId, args.type, args.entries);
  }

  get $typeId(): string {
    return Stage.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): Stage {
    return Stage.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      type: toEnumFromString(MapUtils.getString(map, 'type'), StageType),
      entries: MapUtils.getList<StageEntry>(
          map,
          'entries',
          (value) => context.fromData<StageEntry>(JSONUtils.decodeRecord(value)),
      ),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'type': fromEnumAsString(this.type),
      'entries': this.entries.map((entry) => context.toData(entry)),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    type = this.type,
    entries = cloneListOfCloneable(this.entries),
  }: {
    uniqueId?: string,
    type?: StageType,
    entries?: StageEntry[],
  } = {}): this {
    return Stage.create({ uniqueId, type, entries }) as this;
  }

  isSame(other: Stage): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.type === this.type &&
        isSameList(other.entries, this.entries, isSameData);
  }
}

export class StageBuilder extends DataModelBuilder<Stage> {
  build(context: SerializationContext, map: SerializedData): Stage {
    return Stage.fromMap(context, map);
  }
}
